import React, { useEffect, useState, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Box from "@mui/material/Box";
import { faltou, findByPresenca, presente } from "./api-presenca-aluno";
import { errorMessage } from "../../util/ConfirmAlertUtil";
import Loading from "../UI/Loading";
import Switch from "../UI/Switch";

const PRESENTE_CODIGO = 1;

const PresencaAlunoTableSwitch = ({ presenca }) => {
  const [presencaAlunos, setPresencaAlunos] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [updatingCodigo, setUpdatingCodigo] = useState(null);

  useEffect(() => {
    let ativo = true;

    const carregarPresencas = async () => {
      if (!presenca?.codigo) return;
      setIsLoading(true);
      try {
        const data = await findByPresenca(presenca.codigo);
        if (!ativo) return;
        if (data.error) {
          errorMessage(data.message);
        } else {
          setPresencaAlunos(data);
        }
      } catch (err) {
        if (ativo)
          errorMessage("Não foi possível carregar a lista de presença.");
      } finally {
        if (ativo) setIsLoading(false);
      }
    };

    carregarPresencas();
    return () => {
      ativo = false;
    };
  }, [presenca?.codigo]);

  const updatePresencaAlunos = useCallback((pa) => {
    setPresencaAlunos((prevState) =>
      prevState.map((ps) => (ps.codigo === pa.codigo ? pa : ps)),
    );
  }, []);

  const handleSwitch = useCallback(
    async (pa) => {
      const estaPresente = pa.legendaDiario.codigo === PRESENTE_CODIGO;
      const acao = estaPresente ? faltou : presente;

      setUpdatingCodigo(pa.codigo);
      try {
        const data = await acao(pa.codigo);
        if (data.error) {
          errorMessage(data.message);
        } else {
          updatePresencaAlunos(data);
        }
      } catch (err) {
        errorMessage("Não foi possível atualizar a presença do aluno.");
      } finally {
        setUpdatingCodigo(null);
      }
    },
    [updatePresencaAlunos],
  );

  const alunosOrdenados = useMemo(
    () =>
      [...presencaAlunos].sort(
        (a, b) => a.alunoTurma.numeroChamada - b.alunoTurma.numeroChamada,
      ),
    [presencaAlunos],
  );

  const totalPresentes = useMemo(
    () =>
      presencaAlunos.filter((pa) => pa.legendaDiario.codigo === PRESENTE_CODIGO)
        .length,
    [presencaAlunos],
  );
  const totalAlunos = presencaAlunos.length;

  return (
    <Container component={Paper} sx={{ mt: 3, p: 2, borderRadius: 3 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1,
          mb: 2,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Presença dos Alunos
        </Typography>
        {!isLoading && totalAlunos > 0 && (
          <Box sx={{ display: "flex", gap: 1 }}>
            <Chip
              label={`${totalPresentes} presentes`}
              color="success"
              size="small"
            />
            <Chip
              label={`${totalAlunos - totalPresentes} ausentes`}
              color="error"
              size="small"
            />
          </Box>
        )}
      </Box>

      {isLoading ? (
        <Loading description="Carregando Presença Aluno..." />
      ) : totalAlunos === 0 ? (
        <Box sx={{ p: 4, textAlign: "center", color: "text.secondary" }}>
          <Typography>Nenhum aluno encontrado para esta presença.</Typography>
        </Box>
      ) : (
        <TableContainer
          sx={{
            maxHeight: 520,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Table
            size="small"
            stickyHeader
            aria-label="tabela de presença dos alunos"
          >
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, bgcolor: "grey.100" }}>
                  Nº Chamada
                </TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: "grey.100" }}>
                  Nome
                </TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: "grey.100" }}>
                  Situação
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {alunosOrdenados.map((pa) => {
                const estaPresente =
                  pa.legendaDiario.codigo === PRESENTE_CODIGO;
                return (
                  <TableRow
                    key={pa.codigo}
                    hover
                    sx={{ "&:nth-of-type(odd)": { bgcolor: "action.hover" } }}
                  >
                    <TableCell>{pa.alunoTurma.numeroChamada}</TableCell>
                    <TableCell>{pa.aluno.nome}</TableCell>
                    <TableCell>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Switch
                          size="small"
                          handleSwitch={() => handleSwitch(pa)}
                          value={estaPresente}
                          disabled={updatingCodigo === pa.codigo}
                          name="presente"
                          aria-label={`Marcar presença de ${pa.aluno.nome}`}
                        />
                        <Chip
                          label={pa.legendaDiario.descricao}
                          size="small"
                          color={estaPresente ? "success" : "error"}
                          variant="outlined"
                        />
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Container>
  );
};

PresencaAlunoTableSwitch.propTypes = {
  presenca: PropTypes.shape({
    codigo: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
};

export default PresencaAlunoTableSwitch;
